// LicitaIA — Lightweight, cookieless web traffic tracker.
// Generates a random session ID held in localStorage (no PII), sends a
// pageview when the route changes and a heartbeat every 15 s so the admin
// "Visitas" dashboard can show who is online right now. Time spent on each
// page is computed client-side and sent on the next pageview / on unload.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const SESSION_KEY = "licitaia_tracker_sid";
const HEARTBEAT_MS = 15000;

function newSid() {
    const rand = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`)
        .replace(/-/g, "")
        .slice(0, 24);
    return `s_${rand}`;
}

function getSid() {
    try {
        let sid = localStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = newSid();
            localStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    } catch {
        return newSid();
    }
}

function readUtm() {
    try {
        const u = new URL(window.location.href);
        return {
            utm_source: u.searchParams.get("utm_source") || "",
            utm_medium: u.searchParams.get("utm_medium") || "",
            utm_campaign: u.searchParams.get("utm_campaign") || "",
            utm_term: u.searchParams.get("utm_term") || "",
            utm_content: u.searchParams.get("utm_content") || "",
        };
    } catch {
        return {};
    }
}

let lastPath = null;
let lastEnterAt = 0;
let lastUtm = {};
let heartbeatTimer = null;
let started = false;

async function send(payload) {
    if (!BACKEND_URL) return;
    try {
        await fetch(`${BACKEND_URL}/api/track`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: true,
        });
    } catch {
        /* silent */
    }
}

function sendPageview(path) {
    const sid = getSid();
    const prevDuration = lastEnterAt ? Date.now() - lastEnterAt : 0;
    const utm = readUtm();
    if (utm.utm_source || utm.utm_campaign) lastUtm = utm;
    send({
        session_id: sid,
        path,
        event: "pageview",
        referrer: document.referrer || "",
        duration_ms: prevDuration,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        utm_source: lastUtm.utm_source || "",
        utm_medium: lastUtm.utm_medium || "",
        utm_campaign: lastUtm.utm_campaign || "",
        utm_term: lastUtm.utm_term || "",
        utm_content: lastUtm.utm_content || "",
    });
    lastEnterAt = Date.now();
}

function sendHeartbeat() {
    if (!lastPath) return;
    send({
        session_id: getSid(),
        path: lastPath,
        event: "heartbeat",
    });
}

function sendUnload() {
    if (!lastPath) return;
    const sid = getSid();
    const duration = lastEnterAt ? Date.now() - lastEnterAt : 0;
    // navigator.sendBeacon is the canonical way to flush during unload.
    try {
        const blob = new Blob(
            [
                JSON.stringify({
                    session_id: sid,
                    path: lastPath,
                    event: "unload",
                    duration_ms: duration,
                }),
            ],
            { type: "application/json" },
        );
        navigator.sendBeacon(`${BACKEND_URL}/api/track`, blob);
    } catch {
        /* ignore */
    }
}

export function trackPageview(path) {
    if (!path) return;
    // Skip duplicate fires for the same path within 1 s (StrictMode etc.)
    if (path === lastPath && Date.now() - lastEnterAt < 1000) return;
    lastPath = path;
    sendPageview(path);
    if (!started) {
        started = true;
        heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_MS);
        window.addEventListener("beforeunload", sendUnload);
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") sendUnload();
        });
    }
}

export function trackEvent(cta) {
    if (!cta) return;
    send({
        session_id: getSid(),
        path: lastPath || window.location.pathname,
        event: "cta",
        cta: String(cta).slice(0, 40),
    });
}

export function stopTracker() {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
    started = false;
}
